import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import InsightCard from "@/components/InsightCard";
import ReadinessBar from "@/components/ReadinessBar";
import DraftWatermarkOverlay from "@/components/DraftWatermarkOverlay";
import { Download, Clock, ShieldCheck, Send, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { API, api } from "@/lib/api";
import { DISCLAIMER_TEXT, SCORE_CAVEAT_TEXT } from "@/lib/constants";
import ResilienceScore from "@/components/ResilienceScore";
import TopRisksBlock from "@/components/summary/TopRisksBlock";
import CallToActionBlock from "@/components/summary/CallToActionBlock";

const READINESS_KEYS = [
  "operational_flexibility",
  "communications_readiness",
  "vendor_recovery",
  "data_protection",
  "staffing_resilience",
];

export default function SharedView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/shared/${token}`);
        setData(data);
      } catch (e) {
        if (e?.response?.status === 410) {
          setExpired(true);
        } else {
          const msg = e?.response?.data?.detail || "Share link not found.";
          setError(msg);
          toast.error(msg);
        }
      }
    })();
  }, [token]);

  if (expired) {
    return <ExpiredView token={token} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
        <Header minimal />
        <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-20">
          <h1 className="font-display text-2xl font-semibold text-slate-900 dark:text-slate-50">Link unavailable</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{error}</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
        <Header minimal />
        <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-20">
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading shared assessment…</p>
        </main>
      </div>
    );
  }

  const r = data.report;
  const s = r?.sections || {};
  const expires = data.expires_at ? new Date(data.expires_at) : null;
  const severeCount = (data.insights || []).filter((i) => i.severity === "severe" || i.severity === "high").length;
  const isDraft = !!data.access?.draft_watermark;

  const Body = (
    <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Shared read-only view</p>
            </div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
              Resilience Readiness Assessment
            </h1>
            <p className="mt-2 text-slate-600 dark:text-slate-300 text-sm">
              {r?.organization} · {r?.generated_at}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {expires && (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                <Clock className="h-3.5 w-3.5" /> Link expires {expires.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
              </span>
            )}
            <a
              href={`${API}/shared/${token}/pdf`}
              data-testid="shared-pdf-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
            >
              <Download className="h-4 w-4" /> Download PDF
            </a>
          </div>
        </div>

        {/* Acquisition banner */}
        <div data-testid="acquisition-banner" className="mt-6 rounded-xl border border-teal-200 dark:border-teal-500/30 bg-teal-50/70 dark:bg-teal-500/10 p-4 flex items-start gap-3">
          <Sparkles className="h-4 w-4 text-teal-700 dark:text-teal-300 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-slate-900 dark:text-slate-50">
              <b>Want your own resilience plan?</b> Run a free 10-minute guided assessment for your organisation — no signup required.
            </p>
          </div>
          <Link
            to="/"
            data-testid="acquisition-cta"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-xs font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors whitespace-nowrap"
          >
            Start free <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {severeCount > 0 && (
          <div className="mt-6 rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4 flex items-start gap-3">
            <div className="h-2 w-2 rounded-full bg-amber-600 mt-2" />
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-50">{severeCount} elevated risk{severeCount > 1 ? "s" : ""} flagged</p>
              <p className="text-xs text-slate-700 dark:text-slate-300 mt-0.5">Review the items below; a qualified consultant is recommended for validation.</p>
            </div>
          </div>
        )}

        {/* Rule 9 — disclaimer before all content. */}
        <div className="mt-6 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-5" data-testid="shared-disclaimer-top">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Please read first</p>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{s.disclaimer || DISCLAIMER_TEXT}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <section className="lg:col-span-7 space-y-6">
            <Block title="Executive Summary"><p className="text-slate-700 dark:text-slate-200 leading-relaxed">{s.executive_summary}</p></Block>
            <TopRisksBlock items={s.top_risks} />
            <Block title="Dependency Analysis">
              {(s.dependency_analysis || "").split("\n\n").map((p, i) => <p key={i} className="text-slate-700 dark:text-slate-200 leading-relaxed mb-3">{p}</p>)}
            </Block>
            <BulletBlock title="Single Points of Failure" items={s.single_points_of_failure} />
            <BulletBlock title="Continuity Strategies" items={s.continuity_strategies} />
            <BulletBlock title="Recovery Priorities" items={s.recovery_priorities} ordered />
            <BulletBlock title="Recommendations" items={s.recommendations} />
          </section>
          <aside className="lg:col-span-5 space-y-6">
            <ResilienceScore readiness={data.readiness || {}} />
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900 p-5">
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">Observations</p>
              <div className="mt-4 space-y-3">
                {(data.insights || []).map((ins, i) => <InsightCard key={i} insight={ins} index={i} />)}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">Readiness Indicators</p>
              <div className="mt-4 space-y-4">
                {READINESS_KEYS.map((k) => <ReadinessBar key={k} category={k} score={data.readiness?.[k] ?? 60} />)}
              </div>
              <p data-testid="shared-score-caveat" className="mt-4 text-xs text-slate-500 dark:text-slate-400 leading-relaxed border-t border-slate-100 dark:border-slate-800 pt-3">
                {s.score_caveat || SCORE_CAVEAT_TEXT}
              </p>
            </div>
          </aside>
        </div>

        {/* Rule 11 — closing call-to-action is the final block. */}
        <CallToActionBlock cta={s.call_to_action} onBook={null} />
      </main>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header minimal />
      {isDraft ? (
        <DraftWatermarkOverlay>
          {Body}
        </DraftWatermarkOverlay>
      ) : Body}
      <Footer />
    </div>
  );
}

function ExpiredView({ token }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Please share your name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email.");
    try {
      setSending(true);
      await api.post(`/shared/${token}/request-renewal`, {
        recipient_name: name.trim(),
        recipient_email: email.trim(),
        message: message.trim() || null,
      });
      setSent(true);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header minimal />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-16">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Link expired</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
          {sent ? "Request sent" : "This share link has expired"}
        </h1>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          {sent
            ? "We've notified the plan owner. If they approve, you'll receive a fresh 30-day access link by email shortly."
            : "Share links expire after 30 days for safety. You can ask the plan owner to send you a fresh one — they just need to click a single approval link."}
        </p>

        {!sent ? (
          <div className="mt-8 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Your name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                data-testid="renewal-name"
                className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Your email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="renewal-email"
                className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Message to the owner (optional)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="A quick note — e.g. why you'd like access again."
                data-testid="renewal-message"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
              />
            </div>
            <button
              onClick={submit}
              disabled={sending}
              data-testid="renewal-submit"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors disabled:opacity-50"
            >
              {sending ? "Sending…" : <>Ask the owner for a fresh link <Send className="h-4 w-4" /></>}
            </button>
          </div>
        ) : (
          <div data-testid="renewal-sent" className="mt-8 rounded-xl border border-teal-300 dark:border-teal-500/40 bg-teal-50 dark:bg-teal-500/10 p-6 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-teal-700 dark:text-teal-300 mt-0.5" />
            <p className="text-sm text-slate-800 dark:text-slate-100">
              Your renewal request was delivered. We've BCC'd nothing — only the owner is notified. If they approve, your fresh link will arrive at <b>{email}</b>.
            </p>
          </div>
        )}

        <div className="mt-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-teal-600 dark:text-teal-400" />
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Want your own?</p>
          </div>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            Free 10-minute guided assessment for your own organisation — operational, plain-English, ready to share.
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            data-testid="expired-acquisition-cta"
          >
            Start your own assessment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
      <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function BulletBlock({ title, items, ordered = false }) {
  if (!items || !items.length) return null;
  const Tag = ordered ? "ol" : "ul";
  return (
    <Block title={title}>
      <Tag className={`${ordered ? "list-decimal" : "list-disc"} list-inside space-y-2 text-slate-700 dark:text-slate-200 leading-relaxed`}>
        {items.map((it, idx) => <li key={idx}>{it}</li>)}
      </Tag>
    </Block>
  );
}
